import { t } from "i18next";
import { useComicData } from "../hooks/hookstate/useComicData";
import { SLIDE_IN_MENUS } from "../../root/constants";
import { Button, Flex, Link, Tooltip } from "@chakra-ui/react";
import { GearIcon } from "../../../components/icons/GearIcon";
import { useSlideInMenu } from "../../../components/slideInMenu/hooks/useSlideInMenu";
import { Artist, Comic, ServerSingleResponse, User } from "../../../types";
import { useQuery, skipToken } from "@tanstack/react-query";
import { ReleaseInfoMenuTitle } from "../../../components/slideInMenu/releaseInfo/ReleaseInfoMenuTitle";
import { useReleaseCommentsData } from "../hooks/hookstate/useRelatedReleases";
import { EditIcon } from "../../../components/icons/EditIcon";
import { useUserData } from "../../../hooks/useUserData";
import { AxiosResponse } from "axios";

export const menuTitles = {
  [SLIDE_IN_MENUS.settings]: <SettingsMenuTitle />,
  [SLIDE_IN_MENUS.allPages]: <AllPagesMenuTitle />,
  [SLIDE_IN_MENUS.addTo]: <AddToMenuTitle />,
  [SLIDE_IN_MENUS.comments]: <CommentsMenuTitle />,
  [SLIDE_IN_MENUS.comicDetails]: <ComicDetailsMenuTitle />,
  [SLIDE_IN_MENUS.publisher]: <PublisherProfileMenuTitle />,
  [SLIDE_IN_MENUS.releaseInfo]: <ReleaseInfoMenuTitle />,
  [SLIDE_IN_MENUS.userProfile]: <UserProfileMenuTitle />,
  [SLIDE_IN_MENUS.bundleDetails]: <BundleDetailsMenuTitle />,
};

export function TitleWithGearIcon({
  editUrl,
  title,
  label,
  enabled,
}: {
  enabled: boolean;
  editUrl: string;
  title: string;
  label: string;
}) {
  return (
    <Flex
      width="100%"
      justifyContent="space-between"
      alignItems="center"
      pr="8px"
    >
      {title}
      {enabled && (
        <Tooltip
          variant="navbar"
          label={label}
          aria-label={label}
          placement="bottom"
          gutter={8}
        >
          <a href={editUrl}>
            <GearIcon
              transition="color .1s linear"
              _hover={{ color: "blaze.400" }}
              color="dune.100"
              boxSize="24px"
            />
          </a>
        </Tooltip>
      )}
    </Flex>
  );
}
export function ComicDetailsMenuTitle() {
  const { userData } = useUserData();
  const { comicSlug } = useSlideInMenu();
  const { data: comicDataResponse } = useQuery<
    AxiosResponse<ServerSingleResponse<Comic>>
  >({
    enabled: !!comicSlug,
    queryKey: ["comicDetailsComic", comicSlug],
    queryFn: skipToken,
    networkMode: "offlineFirst",
  });

  const comicData = comicDataResponse?.data.payload.results;

  return (
    <TitleWithGearIcon
      enabled={!!(comicData?.can_edit || userData?.is_admin)}
      editUrl={comicData?.edit_url ?? ""}
      title={t("components.slideInMenu.comicDetailsMenu.comicDetails")}
      label={t("components.slideInMenu.comicDetailsMenu.editComic")}
    />
  );
}

export function SettingsMenuTitle() {
  return <span>{t("screens.reader.settingsMenu.settings")}</span>;
}
export function AllPagesMenuTitle() {
  return <span>{t("screens.reader.allPagesMenu.all_pages")}</span>;
}
export function AddToMenuTitle() {
  const { comicData } = useComicData();
  return comicData?.library_status ? (
    <span>{t("screens.reader.addToMenu.change_status")}</span>
  ) : (
    <span>{t("screens.reader.addToMenu.add_to_library")}</span>
  );
}
export function CommentsMenuTitle() {
  const { releaseCommentsData } = useReleaseCommentsData();

  let commentCount = 0;
  releaseCommentsData?.forEach((comment) => {
    commentCount += comment.replies.length;
    commentCount++;
  });
  return (
    <span>{`${commentCount} ${t("screens.reader.commentsMenu.comments")}`}</span>
  );
}

export function PublisherProfileMenuTitle() {
  const { artistSlug } = useSlideInMenu();
  const { data: artistDataResponse } = useQuery<
    AxiosResponse<ServerSingleResponse<Artist>>
  >({
    enabled: !!artistSlug,
    queryKey: ["artistProfileArtist", artistSlug],
    queryFn: skipToken,
    networkMode: "offlineFirst",
  });

  const artistData = artistDataResponse?.data.payload.results;
  return (
    <TitleWithGearIcon
      enabled={!!artistData?.can_edit}
      editUrl={artistData?.edit_url ?? ""}
      title={t("screens.artist.publisher_profile")}
      label={t("screens.artist.editArtist")}
    />
  );
}

export function UserProfileMenuTitle() {
  const { userData: currentUserData } = useUserData();
  const { username } = useSlideInMenu();
  const { data: userDataResponse } = useQuery<
    AxiosResponse<ServerSingleResponse<User>>
  >({
    enabled: !!username,
    queryKey: ["userProfileUser", username],
    queryFn: skipToken,
    networkMode: "offlineFirst",
  });

  const userData = userDataResponse?.data.payload.results;
  if (currentUserData?.id !== userData?.id) {
    return null;
  }

  return (
    <Flex width="full" justifyContent="flex-end">
      <Link href={`${userData?.url}`}>
        <Button
          minWidth="none"
          variant="iconOnly"
          aria-label="Edit user profile"
          paddingRight="16px"
        >
          <EditIcon boxSize="24px" />
        </Button>
      </Link>
    </Flex>
  );
}

export function BundleDetailsMenuTitle() {
  return <span>{t("components.slideInMenu.bundleDetails.bundleDetails")}</span>;
}
